/**
 * Counts the applications that depend on a database (base) from a workspace's
 * application list, per the base-centric export model (SPEC E1/E4): workflows
 * (automations), apps and scripts always travel with their base and are only
 * shown as counts, never individually selectable.
 */

const DEPENDENT_DATABASE_ID = {
  automation: (application) => application.parent_database_id,
  app: (application) => application.database_id,
  scripts: (application) => application.database_id,
}

const COUNT_KEYS = {
  automation: 'workflows',
  app: 'apps',
  scripts: 'scripts',
}

export const emptyDependentCounts = () => ({
  workflows: 0,
  apps: 0,
  scripts: 0,
})

/**
 * @param applications all applications of a workspace (store shape).
 * @returns object mapping database id -> { workflows, apps, scripts }.
 *   Every database in the list gets an entry, even without dependents.
 */
export function dependentCountsByDatabase(applications) {
  const counts = {}
  for (const application of applications) {
    if (application.type === 'database') {
      counts[application.id] = counts[application.id] || emptyDependentCounts()
      continue
    }
    const getDatabaseId = DEPENDENT_DATABASE_ID[application.type]
    if (!getDatabaseId) {
      continue
    }
    const databaseId = getDatabaseId(application)
    if (databaseId == null) {
      continue
    }
    counts[databaseId] = counts[databaseId] || emptyDependentCounts()
    counts[databaseId][COUNT_KEYS[application.type]] += 1
  }
  return counts
}

export function dependentCountsForDatabase(applications, databaseId) {
  return (
    dependentCountsByDatabase(applications)[databaseId] ||
    emptyDependentCounts()
  )
}

/**
 * Returns the non-zero counts as [key, count] pairs in display order
 * (workflows, apps, scripts), e.g. [['workflows', 2], ['apps', 1]], so the
 * dialogs can render "incl. 2 workflows, 1 app" and skip empty kinds.
 */
export function dependentCountParts(counts) {
  return [
    ['workflows', counts?.workflows || 0],
    ['apps', counts?.apps || 0],
    ['scripts', counts?.scripts || 0],
  ].filter(([, count]) => count > 0)
}
